import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Chip, IconButton, Alert, CircularProgress, Card, CardContent,
  Grid, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, EventAvailable as PeriodIcon
} from '@mui/icons-material';
import {
  getBenefitPlans, createBenefitPlan, updateBenefitPlan,
  getEnrollmentPeriods, createEnrollmentPeriod, updateEnrollmentPeriod
} from '../api';

const CATEGORIES = [
  { value: 'health', label: 'Health Insurance' },
  { value: 'dental', label: 'Dental Insurance' },
  { value: 'vision', label: 'Vision Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'disability', label: 'Disability Insurance' },
  { value: '401k', label: '401(k)' },
  { value: 'roth_401k', label: 'Roth 401(k)' },
  { value: 'simple_ira', label: 'SIMPLE IRA' },
  { value: 'hsa', label: 'HSA' },
  { value: 'fsa', label: 'FSA' },
  { value: 'other', label: 'Other' },
];

const PLAN_TYPES = [
  { value: 'employee_only', label: 'Employee Only' },
  { value: 'employee_spouse', label: 'Employee + Spouse' },
  { value: 'employee_children', label: 'Employee + Children' },
  { value: 'family', label: 'Family' },
];

const TAX_TREATMENTS = [
  { value: 'pre_tax', label: 'Pre-Tax' },
  { value: 'post_tax', label: 'Post-Tax' },
  { value: 'roth', label: 'Roth (Post-Tax)' },
];

const PERIOD_TYPES = [
  { value: 'open_enrollment', label: 'Open Enrollment' },
  { value: 'new_hire', label: 'New Hire' },
  { value: 'qualifying_event', label: 'Qualifying Life Event' },
];

const categoryColor = (cat) => {
  const map = { health: 'primary', dental: 'secondary', vision: 'info', life: 'warning',
    disability: 'error', '401k': 'success', roth_401k: 'success', simple_ira: 'success', hsa: 'info', fsa: 'info', other: 'default' };
  return map[cat] || 'default';
};

const fmt = (val) => parseFloat(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function BenefitPlans() {
  const [activeTab, setActiveTab] = useState(0);
  const [plans, setPlans] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Plan dialog
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({});

  // Period dialog
  const [periodDialog, setPeriodDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [periodForm, setPeriodForm] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, periodsRes] = await Promise.all([
        getBenefitPlans(), getEnrollmentPeriods()
      ]);
      setPlans(plansRes.data);
      setPeriods(periodsRes.data);
    } catch (err) {
      setError('Failed to load benefits data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Plan handlers
  const openPlanDialog = (plan = null) => {
    setEditingPlan(plan);
    setPlanForm(plan ? { ...plan } : {
      name: '', category: 'health', plan_type: 'employee_only', provider_name: '',
      plan_number: '', monthly_employee_cost: '', monthly_employer_cost: '',
      deduction_frequency: 'per_paycheck', tax_treatment: 'pre_tax', description: '',
      contribution_rate: '', employer_match_rate: ''
    });
    setPlanDialog(true);
  };

  const savePlan = async () => {
    try {
      if (editingPlan) {
        await updateBenefitPlan(editingPlan.id, planForm);
      } else {
        await createBenefitPlan(planForm);
      }
      setPlanDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save plan');
    }
  };

  // Period handlers
  const openPeriodDialog = (period = null) => {
    setEditingPeriod(period);
    setPeriodForm(period ? { ...period,
      start_date: period.start_date?.substring(0, 10),
      end_date: period.end_date?.substring(0, 10)
    } : {
      name: '', start_date: '', end_date: '', period_type: 'open_enrollment'
    });
    setPeriodDialog(true);
  };

  const savePeriod = async () => {
    try {
      if (editingPeriod) {
        await updateEnrollmentPeriod(editingPeriod.id, periodForm);
      } else {
        await createEnrollmentPeriod(periodForm);
      }
      setPeriodDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save enrollment period');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Benefits Management</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Benefit Plans (${plans.length})`} />
        <Tab label={`Enrollment Periods (${periods.length})`} />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openPlanDialog()}>
              Add Plan
            </Button>
          </Box>

          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {CATEGORIES.filter(c => plans.some(p => p.category === c.value)).map(cat => {
              const catPlans = plans.filter(p => p.category === cat.value);
              const active = catPlans.filter(p => p.is_active).length;
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={cat.value}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">{cat.label}</Typography>
                      <Typography variant="h6">{active} <Typography component="span" variant="caption">/ {catPlans.length}</Typography></Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Employee Cost/mo</TableCell>
                  <TableCell align="right">Employer Cost/mo</TableCell>
                  <TableCell>Tax</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(plan => (
                  <TableRow key={plan.id} sx={{ opacity: plan.is_active ? 1 : 0.5 }}>
                    <TableCell><Typography fontWeight="medium">{plan.name}</Typography></TableCell>
                    <TableCell>
                      <Chip label={CATEGORIES.find(c => c.value === plan.category)?.label || plan.category}
                            color={categoryColor(plan.category)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{PLAN_TYPES.find(t => t.value === plan.plan_type)?.label || plan.plan_type}</TableCell>
                    <TableCell>{plan.provider_name || '—'}</TableCell>
                    <TableCell align="right">{fmt(plan.monthly_employee_cost)}</TableCell>
                    <TableCell align="right">{fmt(plan.monthly_employer_cost)}</TableCell>
                    <TableCell>
                      <Chip label={TAX_TREATMENTS.find(t => t.value === plan.tax_treatment)?.label || plan.tax_treatment}
                            size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={plan.is_active ? 'Active' : 'Inactive'}
                            color={plan.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openPlanDialog(plan)}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {plans.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center">No benefit plans yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<PeriodIcon />} onClick={() => openPeriodDialog()}>
              Add Enrollment Period
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map(period => {
                  const now = new Date().toISOString().substring(0, 10);
                  const isCurrentlyOpen = period.is_active && period.start_date?.substring(0, 10) <= now && period.end_date?.substring(0, 10) >= now;
                  return (
                    <TableRow key={period.id}>
                      <TableCell><Typography fontWeight="medium">{period.name}</Typography></TableCell>
                      <TableCell>
                        <Chip label={PERIOD_TYPES.find(t => t.value === period.period_type)?.label || period.period_type}
                              size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{period.start_date ? new Date(period.start_date).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{period.end_date ? new Date(period.end_date).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        <Chip label={isCurrentlyOpen ? 'Open Now' : period.is_active ? 'Active' : 'Closed'}
                              color={isCurrentlyOpen ? 'success' : period.is_active ? 'info' : 'default'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openPeriodDialog(period)}><EditIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {periods.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">No enrollment periods yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Plan Dialog */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Benefit Plan' : 'Create Benefit Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Plan Name" value={planForm.name || ''} required
                onChange={e => setPlanForm({ ...planForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={planForm.category || 'health'} label="Category"
                  onChange={e => setPlanForm({ ...planForm, category: e.target.value })}>
                  {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select value={planForm.plan_type || 'employee_only'} label="Plan Type"
                  onChange={e => setPlanForm({ ...planForm, plan_type: e.target.value })}>
                  {PLAN_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Provider Name" value={planForm.provider_name || ''}
                onChange={e => setPlanForm({ ...planForm, provider_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Plan/Group Number" value={planForm.plan_number || ''}
                onChange={e => setPlanForm({ ...planForm, plan_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Employee Cost / Month" type="number" value={planForm.monthly_employee_cost || ''}
                onChange={e => setPlanForm({ ...planForm, monthly_employee_cost: e.target.value })}
                InputProps={{ startAdornment: '$' }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Employer Cost / Month" type="number" value={planForm.monthly_employer_cost || ''}
                onChange={e => setPlanForm({ ...planForm, monthly_employer_cost: e.target.value })}
                InputProps={{ startAdornment: '$' }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tax Treatment</InputLabel>
                <Select value={planForm.tax_treatment || 'pre_tax'} label="Tax Treatment"
                  onChange={e => setPlanForm({ ...planForm, tax_treatment: e.target.value })}>
                  {TAX_TREATMENTS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {['401k', 'roth_401k', 'simple_ira', 'hsa', 'other'].includes(planForm.category) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Employee Contribution Rate (%)" type="number"
                    value={planForm.contribution_rate || ''}
                    onChange={e => setPlanForm({ ...planForm, contribution_rate: e.target.value })}
                    InputProps={{ endAdornment: '%' }}
                    helperText="Percentage of gross pay per paycheck (e.g., 3 for 3%)" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Employer Match Rate (%)" type="number"
                    value={planForm.employer_match_rate || ''}
                    onChange={e => setPlanForm({ ...planForm, employer_match_rate: e.target.value })}
                    InputProps={{ endAdornment: '%' }}
                    helperText="Employer matches employee contribution up to this % of gross" />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} value={planForm.description || ''}
                onChange={e => setPlanForm({ ...planForm, description: e.target.value })} />
            </Grid>
            {editingPlan && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={planForm.is_active !== false}
                    onChange={e => setPlanForm({ ...planForm, is_active: e.target.checked })} />}
                  label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan} disabled={!planForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Period Dialog */}
      <Dialog open={periodDialog} onClose={() => setPeriodDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPeriod ? 'Edit Enrollment Period' : 'Create Enrollment Period'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Period Name" value={periodForm.name || ''} required
                onChange={e => setPeriodForm({ ...periodForm, name: e.target.value })}
                placeholder="e.g., 2026 Open Enrollment" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Period Type</InputLabel>
                <Select value={periodForm.period_type || 'open_enrollment'} label="Period Type"
                  onChange={e => setPeriodForm({ ...periodForm, period_type: e.target.value })}>
                  {PERIOD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Date" type="date" value={periodForm.start_date || ''}
                onChange={e => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Date" type="date" value={periodForm.end_date || ''}
                onChange={e => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            {editingPeriod && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={periodForm.is_active !== false}
                    onChange={e => setPeriodForm({ ...periodForm, is_active: e.target.checked })} />}
                  label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePeriod}
            disabled={!periodForm.name || !periodForm.start_date || !periodForm.end_date}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
